import React, { useState, useContext, useEffect } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import Button from "components/atoms/Button/Button";
import CardNew from "components/molecules/Card/CardNew";
import errorToast from "helpers/errorToast";
import { getProviderAnalytics } from "api";
import Dropdown from "components/atoms/Dropdown/Dropdown";
import BarChart from "../BarChart";
import GraphDateFilter from "../GraphDateFilter";
import GraphGroupFilter from "../GraphGroupFilter";
import AnalyticsContext from "../analyticsContext";
import OnTimePerformance from "./OnTimePerformance";
import QuotingPerformance from "./QuotingPerformance";
import ChartModeSwitch from "../ChartModeSwitch";
import Rating from "./Rating";
import DateHeaderTable from "../DateHeaderTable";
import ExportXLSXModal from "./ExportXLSXModal";

function Providers() {
  const intl = useIntl();
  const { params } = useContext(AnalyticsContext);

  const [totalSpentData, setTotalSpentData] = useState(null);
  const [shipmentData, setShipmentData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasData, setHasData] = useState(true);
  const [tabValue, setTabValue] = useState("1");

  const [showExportModal, setShowExportModal] = useState(false);

  const tabs = [
    {
      label: intl.formatMessage({
        id: "general.amount",
        defaultMessage: "Amount",
      }),
      value: "1",
    },
    {
      label: intl.formatMessage({
        id: "general.count",
        defaultMessage: "Count",
      }),
      value: "2",
    },
    {
      label: intl.formatMessage({
        id: "report.onTimePerformance",
        defaultMessage: "On Time Performance",
      }),
      value: "3",
    },
    {
      label: intl.formatMessage({
        id: "report.quotingPerformance",
        defaultMessage: "Quoting Performance",
      }),
      value: "4",
    },
    {
      label: intl.formatMessage({
        id: "report.rating",
        defaultMessage: "Rating",
      }),
      value: "5",
    },
  ];

  const getProvidersData = () => {
    setIsLoading(true);
    getProviderAnalytics({ params })
      .then((res) => {
        if (
          Object.keys(res.data.total_spent_data.content).length === 0 ||
          Object.keys(res.data.shipment_data.content).length === 0
        ) {
          setTotalSpentData(null);
          setShipmentData(null);
          setHasData(false);
        } else {
          setTotalSpentData(res.data.total_spent_data);
          setShipmentData(res.data.shipment_data);
          setHasData(true);
        }
        setIsLoading(false);
      })
      .catch((err) => {
        errorToast(err);
      });
  };

  useEffect(() => getProvidersData(), [params]);


  return (
    <>
      <CardNew className="border-radius-top-0">
        <div className="d-flex justify-content-between flex-wrap">
          <div className="mb-xs-2" style={{ width: "140px", display: "inline-block" }}>
            <div className="mb-2">
              <FormattedMessage id="general.report" defaultMessage="Report" />
            </div>
            <div className="d-flex align-items-center">
              <div style={{ minWidth: "140px", marginRight: "50px" }}>
                <Dropdown 
                  options={tabs} 
                  onChange={(item) => setTabValue(item.value)} 
                  defaultValue="1" 
                />
              </div>
              <Button
                variant="link"
                className="export-xlsx__button"
                style={{ whiteSpace: "nowrap" }}
                onClick={() => setShowExportModal(true)}
              >
                <p className="m-0 pl-xxs pr-xxs">
                  <FormattedMessage id="report.export" defaultMessage="Export to XLSX" />
                </p>
              </Button>
            </div>
          </div>
          <div className="d-flex flex-wrap">
            <ChartModeSwitch />
            <GraphDateFilter />
            <GraphGroupFilter />
          </div>
        </div>

        {tabValue === "1" || tabValue === "2" ? (
          <>
            {!hasData && !isLoading && (
              <div className="d-flex flex-column align-items-center justify-content-center" style={{ height: "400px" }}>
                <h5>
                  <FormattedMessage id="analytics.noData" defaultMessage="No data available" />
                </h5>
                <p className="text-secondary">
                  <FormattedMessage id="analytics.changeFilters" defaultMessage="Please change the filters and try again." />
                </p>
              </div>
            )}
            {hasData && !isLoading && (
              <div style={{ width: "100%", marginTop: 30 }}>
                {totalSpentData && tabValue === "1" && (
                  <BarChart
                    chartData={totalSpentData}
                    title={intl.formatMessage({
                      id: "analytics.totalSpentUSD",
                      defaultMessage: "Total Spent USD",
                    })}
                    displayCurrencySymbol
                  />
                )}
                {shipmentData && tabValue === "2" && (
                  <BarChart
                    chartData={shipmentData}
                    title={intl.formatMessage({
                      id: "analytics.numberOfShipments",
                      defaultMessage: "Number of Shipments",
                    })}
                  />
                )}
              </div>
            )}
            {isLoading && (
              <div className="text-center d-flex align-items-center justify-content-center" style={{ height: "400px" }}>
                <div className="spinner-border text-primary" role="status" />
              </div>
            )}
          </>
        ) : null}
      </CardNew>

      {tabValue === "3" && <OnTimePerformance />}
      {tabValue === "4" && <QuotingPerformance />}
      {tabValue === "5" && <Rating />}
      {tabValue === "2" && shipmentData && (
        <DateHeaderTable isLoading={isLoading} chartData={shipmentData} className="mt-5" />
      )}
      {tabValue === "1" && totalSpentData && (
        <DateHeaderTable isLoading={isLoading} chartData={totalSpentData} className="mt-5" displayCurrencySymbol />
      )}
      {showExportModal && (
        <ExportXLSXModal show={showExportModal} onHide={() => setShowExportModal(false)} />
      )}
    </>
  );
}

export default Providers;
